/*
 * <scroll-row> drives a horizontally scrolling list ([data-scroll-row-list])
 * with optional previous/next buttons ([data-scroll-row-step="-1" | "1"]).
 * The buttons show only while the list overflows, disable at either end, and
 * page by most of the visible width (scroll snapping lines up the items). With
 * [data-restore-key] (the category circles), it saves where the row was when
 * one of its links is followed; the next page's same row reopens there,
 * placed before its first paint by the script after it
 * (snippets/collection-subnav.liquid). Touch screens swipe natively. Used by
 * the home page product rows and category circles and by the collection
 * page's subcategory row.
 */

class ScrollRow extends HTMLElement {
  #abort;
  #resizeObserver;
  #frame;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.list = this.querySelector('[data-scroll-row-list]');
    if (!this.list) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.buttons = [...this.querySelectorAll('[data-scroll-row-step]')];
    for (const button of this.buttons) {
      button.addEventListener('click', () => this.#step(Number(button.dataset.scrollRowStep)), { signal });
    }
    this.list.addEventListener('scroll', this.#onScroll, { signal, passive: true });

    // Remember where the row was when one of its links is followed, for the next page's same row.
    if (this.dataset.restoreKey) {
      this.list.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('a[href]')) return;
          try {
            window.sessionStorage.setItem(this.#storageKey, String(Math.round(this.list.scrollLeft)));
          } catch {
            // Storage unavailable: the next page centres the current item instead.
          }
        },
        { signal }
      );
    }

    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this.list);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #storageKey() {
    return `scroll-row:${this.dataset.restoreKey}`;
  }

  // For rows filled after load (assets/product-rows.js): show or hide the buttons for the new content.
  refresh() {
    if (this.list) this.#update();
  }

  #step(direction) {
    this.list.scrollBy({
      left: direction * this.list.clientWidth * 0.8,
      behavior: this.#reducedMotion.matches ? 'instant' : 'smooth',
    });
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const { scrollLeft, clientWidth, scrollWidth } = this.list;
    const overflowing = scrollWidth > clientWidth + 1;

    for (const button of this.buttons) {
      // A button marked data-idle keeps its room while not needed (the category circles: hiding it
      // would move the row); the others leave.
      if (button.hasAttribute('data-idle')) {
        button.dataset.idle = String(!overflowing);
      } else {
        button.hidden = !overflowing;
      }
      button.disabled =
        Number(button.dataset.scrollRowStep) < 0 ? scrollLeft <= 1 : scrollLeft + clientWidth >= scrollWidth - 1;
    }
  }
}

if (!customElements.get('scroll-row')) customElements.define('scroll-row', ScrollRow);
